/**
 * DashboardShell — Dashboard tab P1 (§9 LOCKED 2026-07-28), PLACEHOLDER shell.
 *
 * The Dashboard tab is the user-facing onboarding "Business OS" home — the
 * human-first front door to the platform, in contrast to the agent-oriented
 * Designer / Knowledge / Terminals / Modules / Deploys surfaces. It is the
 * leftmost primary tab and the first-run default viewMode (uiStore).
 *
 * P2 ships the 4-question onboarding wizard: on mount the shell loads the
 * tenant's Business Profile from the server (dashboardStore → GET
 * /api/business-profile/profile, the store of record per §8.1) and renders
 * OnboardingWizard. The Build-Engine fan-out (turning answers into ingest
 * plans / workflows / sites / agents) is P3+.
 *
 * Design doc:
 * docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
 *
 * Chrome parity note: like KnowledgeShell/TerminalsShell, the TopBar is hoisted
 * to App level (2026-07-03) and the per-shell voice strip stays here, so the
 * top nav + voice controls remain visible on this tab.
 */
import { useEffect } from 'react';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { BusinessSwitcher } from './BusinessSwitcher';
import { OnboardingWizard } from './OnboardingWizard';
import { BuildActionCard } from './BuildActionCard';
import { WorkflowsActionCard } from './WorkflowsActionCard';
import { SiteActionCard } from './SiteActionCard';
import { GhlActionCard } from './GhlActionCard';
import { AgentsActionCard } from './AgentsActionCard';
import { BuildProgressBoard } from './BuildProgressBoard';
import { useDashboardStore } from '@/stores/dashboardStore';

export function DashboardShell() {
  const loaded = useDashboardStore((s) => s.loaded);
  const isLoading = useDashboardStore((s) => s.isLoading);
  const loadBusinesses = useDashboardStore((s) => s.loadBusinesses);
  const activeBusinessId = useDashboardStore((s) => s.activeBusinessId);
  const createBusiness = useDashboardStore((s) => s.createBusiness);
  const isSaving = useDashboardStore((s) => s.isSaving);

  // Refresh the businesses from the server once on mount (the server is the
  // store of record). The store's own loaded flag guards against a duplicate
  // fetch under React StrictMode.
  useEffect(() => {
    if (!loaded) void loadBusinesses();
  }, [loaded, loadBusinesses]);

  const hasBusiness = activeBusinessId != null;

  return (
    <div
      data-testid="dashboard-shell-root"
      className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text"
    >
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />
      <div className="flex-1 overflow-y-auto flex flex-col items-center gap-6 p-8">
        {isLoading && !loaded ? (
          <p data-testid="dashboard-loading" className="text-sm text-vibe-text-secondary mt-8">
            Loading your businesses…
          </p>
        ) : !hasBusiness ? (
          <div data-testid="dashboard-empty" className="max-w-xl w-full space-y-4 mt-8 text-center">
            <h1 className="text-xl font-semibold text-vibe-text">Create your first business</h1>
            <p className="text-sm text-vibe-text-secondary">
              Name a business to set up. We&apos;ll create a project for it and turn your answers
              into data, workflows, sites, and agents.
            </p>
            <button
              type="button"
              data-testid="dashboard-empty-create"
              disabled={isSaving}
              onClick={() => {
                const name = window.prompt('Name your business');
                if (name && name.trim()) void createBusiness(name.trim());
              }}
              className="rounded bg-vibe-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {isSaving ? 'Creating…' : 'Create a business'}
            </button>
          </div>
        ) : (
          <>
            <BusinessSwitcher />
            <OnboardingWizard />
            {/* Build Engine action cards — each appears once its answer exists and
                kicks off a Pi routine that composes existing tools:
                  P3 data  → ingest_knowledge (+ Progress board, real status)
                  P4 tasks → n8n workflows / put agents to work
                  P4 biz   → build + publish a site (Deploys)
                  P4 cust  → marketing/CRM (GHL) */}
            <BuildActionCard />
            <WorkflowsActionCard />
            <SiteActionCard />
            <GhlActionCard />
            <AgentsActionCard />
            <BuildProgressBoard />
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardShell;
